function normalizeProductPath(url) {
  if (!url) return '';

  try {
    const base = window.shopUrl || window.location.origin;
    const parsed = new URL(url, base);
    return parsed.pathname.replace(/\/+$/, '').toLowerCase();
  } catch {
    return url.split('?')[0].split('#')[0].replace(/\/+$/, '').toLowerCase();
  }
}

function stripLocalePrefixFromPath(path) {
  return path.replace(/^\/[a-z]{2}(-[a-z]{2})?(\/|$)/i, '/');
}

function getProductHandleFromPath(url) {
  const path = normalizeProductPath(url);
  const match = path.match(/\/products\/([^/]+)/);
  return match ? match[1] : '';
}

function isPdpGalleryDebugEnabled() {
  return (
    window.__ESEOESE_DEBUG_PDP_GALLERY === true ||
    window.localStorage?.getItem('eseoese_debug_pdp_gallery') === '1' ||
    new URLSearchParams(window.location.search).has('debug_pdp_gallery')
  );
}

function isSameProductPage(currentUrl, candidateUrl) {
  if (!currentUrl || !candidateUrl) {
    return currentUrl === candidateUrl;
  }

  const currentPath = normalizeProductPath(currentUrl);
  const candidatePath = normalizeProductPath(candidateUrl);

  if (currentPath === candidatePath) return true;
  if (stripLocalePrefixFromPath(currentPath) === stripLocalePrefixFromPath(candidatePath)) return true;

  const currentHandle = getProductHandleFromPath(currentUrl);
  const candidateHandle = getProductHandleFromPath(candidateUrl);
  return Boolean(currentHandle && candidateHandle && currentHandle === candidateHandle);
}

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.warmProductCatalogCache();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      warmProductCatalogCache() {
        const url = this.dataset.url || window.location.pathname;
        const base = window.shopUrl || window.location.origin;
        const productJsUrl = `${new URL(url, base).pathname.replace(/\/$/, '')}.js`;

        if (!window.__eseoeseProductJsCache) window.__eseoeseProductJsCache = new Map();
        if (window.__eseoeseProductJsCache.has(productJsUrl)) return;

        const promise = fetch(productJsUrl)
          .then((response) => (response.ok ? response.json() : null))
          .then((product) => ({
            variants: product?.variants || [],
            media: product?.media || product?.images || [],
          }))
          .catch(() => ({ variants: [], media: [] }));

        window.__eseoeseProductJsCache.set(productJsUrl, promise);
      }

      shouldDeferGalleryDomSwap(mediaGallery) {
        if (!mediaGallery) return false;
        if (mediaGallery.dataset.variantImageGrouping === 'true') return true;
        if (mediaGallery.dataset.variantImageGroupingSource === 'legacy-js') return true;
        if (mediaGallery.dataset.galleryGroupingReady === 'true') return true;
        if (
          mediaGallery.dataset.desktopLayout === 'columns' &&
          typeof mediaGallery.inferGroupingOptionFromPicker === 'function' &&
          mediaGallery.inferGroupingOptionFromPicker()
        ) {
          return true;
        }
        return false;
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const rawProductUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = rawProductUrl;
        const shouldSwapProduct = !isSameProductPage(this.dataset.url, rawProductUrl);
        const productUrl = shouldSwapProduct ? rawProductUrl : this.dataset.url;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const requestUrl = this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage);

        if (isPdpGalleryDebugEnabled()) {
          console.groupCollapsed('[PDP gallery] optionValueSelectionChange');
          console.log('dataset.url', this.dataset.url);
          console.log('rawProductUrl', rawProductUrl);
          console.log('productUrl', productUrl);
          console.log('shouldSwapProduct', shouldSwapProduct);
          console.log('shouldFetchFullPage', shouldFetchFullPage);
          console.log('requestUrl', requestUrl);
          console.log('callback', shouldSwapProduct ? 'swap' : 'update');
          console.groupEnd();
        }

        this.renderProductInfo({
          requestUrl,
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const productInfoSource = html.querySelector(selector);
          const variant = this.getSelectedVariant(productInfoSource);

          const resolveSwappedProductInfo = (newNode) => {
            if (!newNode) return null;
            if (updateFullPage) {
              return (
                newNode.querySelector?.("product-info[id^='MainProduct']") ||
                document.querySelector("product-info[id^='MainProduct']")
              );
            }
            return newNode.matches?.('product-info') ? newNode : newNode.querySelector('product-info');
          };

          const swappedPostProcess = (newNode) => {
            this.postProcessHtmlCallbacks.forEach((callback) => callback(newNode));

            const productInfoEl = resolveSwappedProductInfo(newNode);
            if (!productInfoEl) return;

            const urlForHistory = productInfoEl.dataset.url || productUrl;
            productInfoEl.updateURL(urlForHistory, variant?.id);
            productInfoEl.pickupAvailability?.update(variant);

            if (!variant) {
              productInfoEl.setUnavailable();
            } else {
              productInfoEl.finalizeVariantUpdate(html, variant);
            }

            productInfoEl.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
          };

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              [swappedPostProcess]
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              productInfoSource,
              this.preProcessHtmlCallbacks,
              [swappedPostProcess]
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.finalizeVariantUpdate(html, variant);
        };
      }

      finalizeVariantUpdate(html, variant) {
        this.updateMedia(html, variant?.featured_media?.id);

        const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
          const source = html.getElementById(`${id}-${this.sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.section}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('hidden', shouldHide(source));
          }
        };

        updateSourceFromDestination('price');
        updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
        updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
        updateSourceFromDestination('Volume');
        updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

        this.updateQuantityRules(this.sectionId, html);
        this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
        this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

        const sourceSubmitButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        const isSubmitDisabled = sourceSubmitButton?.hasAttribute('disabled') ?? true;
        const submitButtonText = sourceSubmitButton?.querySelector('span')?.textContent?.trim();

        this.productForm?.toggleSubmitButton(isSubmitDisabled, submitButtonText);
        if (sourceSubmitButton && this.productForm?.submitButton) {
          this.productForm.submitButton.classList.toggle(
            'hidden',
            sourceSubmitButton.classList.contains('hidden')
          );
        }

        const sourceVariantInput = html.querySelector(
          `#product-form-${this.sectionId} input[name="id"], #product-form-${this.dataset.section} input[name="id"]`
        );
        this.querySelectorAll(`#product-form-${this.dataset.section} input[name="id"]`).forEach((input) => {
          if (sourceVariantInput?.hasAttribute('disabled')) {
            input.setAttribute('disabled', 'disabled');
          } else {
            input.removeAttribute('disabled');
          }
        });

        if (typeof window.eseoeseSyncPdpNotify === 'function') {
          window.eseoeseSyncPdpNotify(variant);
        }

        if (isPdpGalleryDebugEnabled()) {
          console.groupCollapsed('[PDP gallery] finalizeVariantUpdate');
          console.log('variantId', variant?.id);
          console.log('featuredMediaId', variant?.featured_media?.id);
          console.log('variantChangePublished', true);
          console.groupEnd();
        }

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.sectionId,
            html,
            variant,
          },
        });
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        const searchParams = new URLSearchParams();
        if (variantId) searchParams.set('variant', variantId);
        if (new URLSearchParams(window.location.search).has('debug_pdp_gallery')) {
          searchParams.set('debug_pdp_gallery', '1');
        }
        const query = searchParams.toString();
        const urlWithQuery = query ? `${url}?${query}` : url;

        this.querySelector('share-button')?.updateUrl(`${window.shopUrl}${urlWithQuery}`);

        if (this.dataset.updateUrl === 'false') return;
        try {
          window.history.replaceState({}, '', urlWithQuery);
        } catch (error) {
          if (isPdpGalleryDebugEnabled()) {
            console.warn('[PDP gallery] history.replaceState failed', error);
          }
        }
      }

      syncFeaturedGallerySlideFromSection(html, variantFeaturedMediaId) {
        const mediaGallery = this.querySelector('media-gallery');
        if (!mediaGallery || !variantFeaturedMediaId) return;

        const sourceList = html.querySelector('media-gallery [id^="GalleryViewer"] ul');
        const destList = mediaGallery.querySelector('[id^="GalleryViewer"] ul');
        if (!sourceList || !destList) return;

        const mediaKey = `${this.dataset.section}-${variantFeaturedMediaId}`;
        let sourceSlide =
          sourceList.querySelector(`[data-media-id="${mediaKey}"]`) ||
          sourceList.querySelector(`[data-media-id$="-${variantFeaturedMediaId}"]`);
        if (!sourceSlide) {
          sourceSlide =
            sourceList.querySelector('.product__media-item.is-active') ||
            sourceList.querySelector('.product__media-item');
        }
        if (!sourceSlide) return;

        const destMatching =
          destList.querySelector(`[data-media-id="${mediaKey}"]`) ||
          destList.querySelector(`[data-media-id$="-${variantFeaturedMediaId}"]`);
        const destFeaturedSlot = destList.querySelector('.product__media-item');
        const newNode = sourceSlide.cloneNode(true);

        if (destMatching) {
          destMatching.replaceWith(newNode);
        } else if (destFeaturedSlot) {
          destFeaturedSlot.replaceWith(newNode);
        } else {
          destList.prepend(newNode);
        }
      }

      setUnavailable() {
        const hasUnselectedOptions = Array.from(this.querySelectorAll('fieldset')).some(fieldset => {
          return !Array.from(fieldset.querySelectorAll('input[type="radio"]')).some(radio => radio.checked);
        });

        if (hasUnselectedOptions) {
          this.productForm?.toggleSubmitButton(false, window.variantStrings.addToCart);
          this.productForm?.submitButton?.classList.remove('hidden');
        } else {
          this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
          this.productForm?.submitButton?.classList.add('hidden');
        }

        if (typeof window.eseoeseSyncPdpNotify === 'function') {
          window.eseoeseSyncPdpNotify(null);
        }

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      syncMediaGalleryList(mediaGallerySource, mediaGalleryDestination) {
        if (!mediaGallerySource || !mediaGalleryDestination) return false;

        const refreshSourceData = () => {
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id], li[data-target]'));
          const sourceSet = new Set(
            mediaGallerySourceItems.map((item) => item.dataset.mediaId || item.dataset.target)
          );
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId || item.dataset.target,
              { item, index },
            ])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
        const mediaGalleryDestinationItems = Array.from(
          mediaGalleryDestination.querySelectorAll('li[data-media-id], li[data-target]')
        );
        const destinationSet = new Set(
          mediaGalleryDestinationItems.map((item) => item.dataset.mediaId || item.dataset.target)
        );
        let shouldRefresh = false;

        for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
          const destinationKey = mediaGalleryDestinationItems[i].dataset.mediaId || mediaGalleryDestinationItems[i].dataset.target;
          if (!sourceSet.has(destinationKey)) {
            mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
            shouldRefresh = true;
          }
        }

        for (let i = 0; i < mediaGallerySourceItems.length; i++) {
          const sourceKey = mediaGallerySourceItems[i].dataset.mediaId || mediaGallerySourceItems[i].dataset.target;
          if (!destinationSet.has(sourceKey)) {
            mediaGallerySourceItems[i].remove();
            shouldRefresh = true;
          }
        }

        if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

        mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
          const destinationKey = destinationItem.dataset.mediaId || destinationItem.dataset.target;
          const sourceData = sourceMap.get(destinationKey);

          if (sourceData && sourceData.index !== destinationIndex) {
            mediaGallerySource.insertBefore(
              sourceData.item,
              mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
            );
            [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          }
        });

        return shouldRefresh;
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallery = this.querySelector('media-gallery');
        const useVariantImageGrouping = mediaGallery?.dataset.variantImageGrouping === 'true';
        const mediaGalleryFromResponse = html.querySelector('media-gallery');

        if (this.shouldDeferGalleryDomSwap(mediaGallery)) {
          this.syncFeaturedGallerySlideFromSection(html, variantFeaturedMediaId);
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          return;
        }

        const reapplyLegacyGrouping = (gallery, variantId) => {
          if (!gallery?.ensureLegacyVariantImageGrouping) return;
          gallery.ensureLegacyVariantImageGrouping().then((enabled) => {
            if (!enabled) {
              gallery.setActiveMedia?.(`${this.dataset.section}-${variantId}`, true);
              return;
            }
            const variant = gallery.getVariantFromProductInfo?.();
            if (variant) gallery.applyVariantImageGrouping?.(variant);
          });
        };

        if (!useVariantImageGrouping && mediaGallery && mediaGalleryFromResponse) {
          HTMLUpdateUtility.viewTransition(
            mediaGallery,
            mediaGalleryFromResponse,
            this.preProcessHtmlCallbacks,
            []
          );
          const modalContent = this.productModal?.querySelector('.product-media-modal__content');
          const newModalContent = html.querySelector('product-modal .product-media-modal__content');
          if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;

          reapplyLegacyGrouping(this.querySelector('media-gallery'), variantFeaturedMediaId);
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          return;
        }

        if (!useVariantImageGrouping) {
          const mediaGallerySource = mediaGallery?.querySelector('[id^="GalleryViewer"] ul');
          const mediaGalleryDestination = html.querySelector(`media-gallery [id^="GalleryViewer"] ul`);

          if (mediaGallerySource && mediaGalleryDestination) {
            this.syncMediaGalleryList(mediaGallerySource, mediaGalleryDestination);
            if (mediaGallerySource.closest('slider-component')?.resetPages) {
              mediaGallerySource.closest('slider-component').resetPages();
            }
          }

          const thumbnailSource = mediaGallery?.querySelector('[id^="GalleryThumbnails"] ul');
          const thumbnailDestination = html.querySelector(`media-gallery [id^="GalleryThumbnails"] ul`);
          if (thumbnailSource && thumbnailDestination) {
            this.syncMediaGalleryList(thumbnailSource, thumbnailDestination);
            if (thumbnailSource.closest('slider-component')?.resetPages) {
              thumbnailSource.closest('slider-component').resetPages();
            }
          }

          if (mediaGalleryDestination) {
            const totalSlides = mediaGalleryDestination.querySelectorAll('li[data-media-id]').length;
            const counterTotal = mediaGallery?.querySelector('.slider-counter--total');
            if (counterTotal && totalSlides > 0) {
              counterTotal.textContent = totalSlides;
            }
          }

          const modalContent = this.productModal?.querySelector('.product-media-modal__content');
          const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
          if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
        }

        if (!useVariantImageGrouping) {
          reapplyLegacyGrouping(mediaGallery, variantFeaturedMediaId);
        }

        if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
